import React, { useState, useEffect } from "react";
import { customFetch } from '../apiClient';

const EditEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const loadEmployees = async () => {
    try {
      setLoading(true);
     const res = await customFetch(`/Employees`);
      if (!res.ok) throw new Error("Failed to fetch employees");
      const data = await res.json();
      setEmployees(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const startEdit = (emp) => {
    setEditId(emp.id);
    setEditData({ ...emp });
    setError("");
    setMessage("");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({});
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
     const res = await customFetch(`/Employees/${editId}`, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify(editData),
});

      if (!res.ok) throw new Error(await res.text());
      setMessage("Employee updated successfully!");
      cancelEdit();
      loadEmployees();
      
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    setLoading(true);
    try {
     const res = await customFetch(`/Employees/${id}`, {
  method: "DELETE"
});
      if (!res.ok) throw new Error(await res.text());
      setMessage("Employee deleted successfully!");
      loadEmployees();
      
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.department?.departmentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.contact1?.includes(searchQuery)
  );

  return (
    <div className="edit-employee-container">
      <div className="edit-employee-card">
        {/* Header */}
        <div className="employee-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-person-badge-fill"></i>
            </div>
            <div className="header-text">
              <h5>Employee Management</h5>
              <p>View, edit and manage employee records</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-people-fill"></i>
              <span>{employees.length} Employees</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
        {message && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage("")}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className="employee-body">
          {/* Toolbar */}
          <div className="table-toolbar">
            <div className="search-box">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by name, role, dept, or contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn-refresh" onClick={loadEmployees} disabled={loading}>
              <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
              Refresh
            </button>
          </div>

          {/* Table */}
          <div className="table-container">
            {loading && employees.length === 0 ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading employees...</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h6>No Employees Found</h6>
                <p>{searchQuery ? "No matches found" : "No employees available"}</p>
              </div>
            ) : (
              <table className="employees-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>Sr</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Department</th>
                    <th>Contact</th>
                    <th style={{ width: '90px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp, i) => (
                    <tr key={emp.id} className={editId === emp.id ? 'editing' : ''}>
                      <td className="text-center">
                        <span className="sr-badge">{i + 1}</span>
                      </td>
                      
                      {editId === emp.id ? (
                        <>
                          <td>
                            <input
                              type="text"
                              value={editData.name}
                              onChange={(e) => handleChange("name", e.target.value)}
                              className="form-input-edit"
                              placeholder="Name"
                            />
                          </td>
                          <td>
                            <select
                              value={editData.type}
                              onChange={(e) => handleChange("type", e.target.value)}
                              className="form-select-edit"
                            >
                              <option value="Doctor">Doctor</option>
                              <option value="Nurse">Nurse</option>
                              <option value="Staff">Staff</option>
                            </select>
                          </td>
                          <td>
                            <div className="department-info">
                              <i className="bi bi-building-check"></i>
                              {editData.department?.departmentName || "-"}
                            </div>
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editData.contact1 || ""}
                              onChange={(e) => handleChange("contact1", e.target.value)}
                              className="form-input-edit"
                              placeholder="Contact"
                            />
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-action btn-save" onClick={saveEdit} disabled={loading} title="Save">
                                <i className="bi bi-check-lg"></i>
                              </button>
                              <button className="btn-action btn-cancel" onClick={cancelEdit} title="Cancel">
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div className="employee-name">
                              <i className="bi bi-person-circle"></i>
                              {emp.name}
                            </div>
                          </td>
                          <td>
                            <span className={`type-badge ${emp.type?.toLowerCase()}`}>
                              <i className={`bi ${
                                emp.type === 'Doctor' ? 'bi-heart-pulse' :
                                emp.type === 'Nurse' ? 'bi-hospital' :
                                'bi-person-gear'
                              }`}></i>
                              {emp.type}
                            </span>
                          </td>
                          <td>
                            <div className="department-info">
                              <i className="bi bi-building-check"></i>
                              {emp.department?.departmentName || "-"}
                            </div>
                          </td>
                          <td>
                            <div className="contact-info">
                              <i className="bi bi-telephone-fill"></i>
                              {emp.contact1 || "-"}
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-icon btn-edit" onClick={() => startEdit(emp)} title="Edit">
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              <button className="btn-icon btn-delete" onClick={() => deleteEmployee(emp.id)} title="Delete">
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .edit-employee-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .edit-employee-card {
          background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0; overflow: hidden;
        }

        /* Animations */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; }

        /* Header */
        .employee-header {
          background: var(--theme-color, #0088cc); padding: 8px 12px;
          display: flex; justify-content: space-between; align-items: center;
          border-bottom: 2px solid #00d4aa; flex-wrap: wrap; gap: 10px;
        }

        .header-left { display: flex; align-items: center; gap: 8px; }
        .header-icon {
          width: 28px; height: 28px; background: rgba(255, 255, 255, 0.2); border-radius: 4px;
          display: flex; align-items: center; justify-content: center; font-size: 14px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .header-stats { display: flex; gap: 8px; }
        .stat-item {
          background: rgba(255, 255, 255, 0.2); padding: 4px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; color: white; font-size: 11px; font-weight: 600;
        }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Body & Toolbar */
        .employee-body { padding: 12px; }

        .table-toolbar {
          padding: 8px 10px; background: #f7fafc; border-radius: 4px; margin-bottom: 12px;
          display: flex; justify-content: space-between; align-items: center; gap: 10px; border: 1px solid #e2e8f0;
        }

        .search-box { flex: 1; max-width: 300px; position: relative; }
        .search-box i { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #a0aec0; font-size: 12px; }
        .search-box input {
          width: 100%; padding: 6px 10px 6px 28px; border: 1px solid #cbd5e0;
          border-radius: 4px; font-size: 11px; outline: none; transition: 0.2s;
        }
        .search-box input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        .btn-refresh {
          padding: 6px 12px; border-radius: 4px; border: 1px solid #cbd5e0;
          background: white; color: #4a5568; font-size: 11px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; gap: 6px; transition: 0.2s;
        }
        .btn-refresh:hover:not(:disabled) { background: #edf2f7; }

        /* Loading & Empty State */
        .loading-state, .empty-state { text-align: center; padding: 40px 10px; color: #718096; font-size: 11px; }
        .spinner {
          width: 24px; height: 24px; border: 3px solid #edf2f7; border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 10px;
        }
        .empty-state h6 { margin: 0 0 4px 0; color: #2d3748; font-size: 13px; font-weight: 600; }
        .empty-state i { font-size: 32px; color: #cbd5e0; margin-bottom: 8px; display: block; }
        .empty-state p { margin: 0; font-style: italic; }

        /* Table Container */
        .table-container { overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid #e2e8f0; border-radius: 4px; }

        /* Table Compact */
        .employees-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .employees-table thead { background: var(--theme-color, #3182ce); }
        .employees-table th { padding: 8px 10px; font-weight: 600; color: white; white-space: nowrap; border-bottom: 2px solid #00d4aa; }
        .employees-table tbody tr { border-bottom: 1px solid #e2e8f0; transition: 0.2s; }
        .employees-table tbody tr:hover { background: #f7fafc; }
        .employees-table tbody tr.editing { background: #ebf8ff; box-shadow: inset 0 0 0 2px var(--theme-color, #3182ce); }
        .employees-table td { padding: 6px 10px; color: #2d3748; vertical-align: middle; }

        .text-center { text-align: center; }
        
        .sr-badge {
          background: var(--theme-color, #3182ce); color: white; padding: 2px 6px;
          border-radius: 3px; font-weight: 600; font-size: 10px; display: inline-block;
        }

        .employee-name, .department-info, .contact-info { display: flex; align-items: center; gap: 6px; font-weight: 500; }
        .employee-name i { color: var(--theme-color, #3182ce); font-size: 12px; }
        .department-info i { color: #38a169; font-size: 12px; }
        .contact-info i { color: #d69e2e; font-size: 12px; }

        .type-badge {
          padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;
        }
        .type-badge.doctor { background: #ebf8ff; color: var(--theme-color, #3182ce); }
        .type-badge.nurse { background: #c6f6d5; color: #22543d; }
        .type-badge.staff { background: #fefcbf; color: #744210; }

        /* Inline Edit Inputs */
        .form-input-edit, .form-select-edit {
          width: 100%; padding: 4px 6px; border: 1px solid var(--theme-color, #3182ce);
          border-radius: 3px; font-size: 11px; outline: none; background: white;
        }
        .form-input-edit:focus, .form-select-edit:focus { box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 4px; justify-content: center; }
        .btn-icon, .btn-action {
          width: 24px; height: 24px; border-radius: 4px; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; font-size: 12px; transition: 0.2s;
        }
        
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }

        .btn-save { background: #c6f6d5; color: #2f855a; }
        .btn-save:hover:not(:disabled) { background: #9ae6b4; }
        .btn-cancel { background: #edf2f7; color: #4a5568; }
        .btn-cancel:hover { background: #e2e8f0; }

        /* Responsive Breakpoints */
        @media (max-width: 600px) {
          .room-header { flex-direction: column; align-items: stretch; text-align: center; }
          .header-left { justify-content: center; }
          .table-toolbar { flex-direction: column; align-items: stretch; }
          .search-box { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default EditEmployeeList;